import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Plus, Edit, Trash2, X, AlertCircle, Loader } from 'lucide-react';
import { useToast } from "../components/ui/toast-provider";

// Servicios
import { PuestoService } from "../services/puestoService";
import { EmpleadoService } from "../services/empleadoService";

// Tipos de Response
import { PuestoResponse} from "../types/puesto";

export default function CrearEmpleado({ isOpen, onGuardado, onClose, idEditando }: { isOpen: boolean; onGuardado: () => void; onClose: () => void; idEditando: number }) {

    const [isLoading, setIsLoading] = useState(true);
    const [puestos, setPuestos] = useState<PuestoResponse[]>([]);
    const [error, setError] = useState("");
    const [editando, setEditando] = useState(false);

    const { addToast } = useToast();

    const [formulario, setFormulario] = useState({
        nombres: '',
        apellidos: '',
        telefono: '',
        correo: '',
        dpi: '',
        idPuesto: '',
        puesto: '',
        activo: true
    });

    const resetFormulario = () => {
        setFormulario({
        nombres: '',
        apellidos: '',
        telefono: '',
        correo: '',
        dpi: '',
        idPuesto: '',
        puesto: '',
        activo: true
        });
    };

    const cargarPuestos = async () => {
        try {
            setIsLoading(true);
            const response = await PuestoService.getAll();
            setPuestos(response.data);
        } catch (err) {
            console.error("Error al cargar puestos: " + err);
        } finally {
            setIsLoading(false);
        }
    }

    const cargarEmpleado = async (id: number) => {
        try {
            setIsLoading(true);
            const response = await EmpleadoService.getById(id);
            const empleado = response.data;
            setFormulario({
                nombres: empleado.nombres,
                apellidos: empleado.apellidos,
                telefono: empleado.telefono,
                correo: empleado.correo,
                dpi: empleado.dpi,
                idPuesto: empleado.idPuesto,
                puesto: empleado.puesto || '',
                activo: empleado.activo
            });
        } catch (err) {
            console.error("Error al cargar empleado: " + err);
        } finally {
            setIsLoading(false);
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            if (editando) {
                await EmpleadoService.actualizar(idEditando, {
                    ...formulario,
                    idPuesto: Number(formulario.idPuesto)
                });
            } else {
                await EmpleadoService.crear({
                    ...formulario,
                    idPuesto: Number(formulario.idPuesto),
                    activo: true
                });
            }

            addToast(editando ? "Empleado actualizado exitosamente" : "Empleado creado exitosamente");
            resetFormulario();
            onGuardado();
        } catch (err:any) {
              const message =
                err?.response?.data?.message ||
                "Error inesperado al guardar empleado";

            setError(message);
        }
    }

    useEffect(() => {
        if(isOpen) {
            resetFormulario();
            if (idEditando !== 0) {
                cargarEmpleado(idEditando);
            }
            setEditando(idEditando !== 0);
            cargarPuestos();
            setError("");
        }
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }
    return (
        <div className="fixed inset-0 bg-opacity-50 flex items-center justify-center z-50">
            <div className="rounded-2xl w-full max-w-2xl shadow-lg relative max-h-[90vh] overflow-y-auto scrollbar-thin">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>{editando ? 'Editar Empleado' : 'Crear Empleado'}</CardTitle>
                        <button
                        onClick={onClose}
                        className="p-2 hover:bg-destructive/10 rounded-lg transition-colors"
                        >
                        <X className="w-5 h-5 text-destructive" />
                        </button>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Input
                            label="Nombres"
                            type="text"
                            value={formulario.nombres}
                            onChange={(e) =>
                                setFormulario({ ...formulario, nombres: e.target.value })
                            }
                            placeholder="Ingrese los nombres"
                            required
                            />
                            <Input
                            label="Apellidos"
                            type="text"
                            value={formulario.apellidos}
                            onChange={(e) =>
                                setFormulario({ ...formulario, apellidos: e.target.value })
                            }
                            placeholder="Ingrese los apellidos"
                            required
                            />
                            <Input
                            label="Teléfono"
                            type="tel"
                            maxLength = {8}
                            value={formulario.telefono}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, ''); // solo números
                                setFormulario({ ...formulario, telefono: value })
                            }}
                            placeholder="00000000"
                            required
                            />
                            <Input
                            label="Correo"
                            type="email"
                            value={formulario.correo}
                            onChange={(e) => {
                                const value = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.target.value);
                                setFormulario({ ...formulario, correo: e.target.value});
                                setError(value ? "" : "El correo no es válido");
                            }}
                            placeholder="correo@ejemplo.com"
                            required
                            />
                            <Input
                            label="DPI"
                            type="text"
                            maxLength={13}
                            value={formulario.dpi}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, ''); // solo números
                                setFormulario({ ...formulario, dpi: value })
                            }}
                            placeholder="0000000000000"
                            required
                            />
                            <Select
                            label="Puesto"
                            value={formulario.idPuesto}
                            onChange={(e) =>
                                setFormulario({ ...formulario, idPuesto: e.target.value })
                            }
                            options={puestos.map((p) => ({ value: (p.idPuesto).toString(), label: p.puesto }))}
                            required
                            />
                        </div>
                        <div className = "flex justify-center">
                        {error && (
                            <span className="text-destructive text-center text-sm">{error}</span>
                        )}
                        </div>
                        <div className="flex gap-2">
                            <Button type="submit" variant="primary">
                            {editando ? 'Editar Empleado' : 'Crear Empleado'}
                            </Button>
                            <Button type="button" variant="ghost" onClick={onClose}>
                            Cancelar
                            </Button>
                        </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    )

}